import { useEffect, useState } from "react";
import { useFormContext, useFormState } from "react-hook-form";
import { useDebounce } from "use-debounce";
import { AddressManualEntry } from "./address-manual-entry";
import { AddressLookup } from "./address-lookup";
import { getAddressData } from "src/api/services/address-service";
import { Text } from "@ucl/react-library";
import { Trans, useTranslation } from "react-i18next";
import { translationMapping } from "src/utils/translation-mappings";
import { AddressResult } from "src/api/address-data-types";

export const AddressSection = () => {
  const { t } = useTranslation();
  const [isFetching, setIsFetching] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState<string>("");
  const [addressData, setAddressData] = useState<AddressResult[]>([]);
  const [isAddressManual, setIsAddressManual] = useState<boolean>(false);

  const { getValues, formState: { dirtyFields }, control, setValue, watch, trigger } = useFormContext();

  const addressPostcodeLookup = watch("address.postcodeLookup")?.trim();
  const [debouncedPostcode] = useDebounce(addressPostcodeLookup, 1000);

  const clearAddressFields = () => {
    setValue(
      "address",
      {
        addressLine1: "",
        addressLine2: "",
        city: "",
        county: "",
        postCode: "",
        country: "United Kingdom",
        addressSelector: "",
        postcodeLookup: getValues("address.postcodeLookup") ?? ""
      },
      { shouldValidate: false }
    );
  };

  const formatAddresses = (results: AddressResult[]) => results.map((address: AddressResult) => ({
    ...address,
    address_qsav: address.address_qsav.replaceAll("|", ", "),
  }));

  const getLookupErrorMessage = (error: any) => {
    if (error.data?.error?.errorCode === "BEES_CUS_02") {
      return t("editContactDetails.errors.postcodeRequired");
    }
    return t("editContactDetails.errors.postcodeError");
  };

  const fetchAddresses = async (postcode: string) => {
    setIsFetching(true);
    try {
      const addresses = await getAddressData(postcode);

      if (addresses.results.length > 0) {
        setAddressData(formatAddresses(addresses.results));
        setAddressLookupError("");
        clearAddressFields();
        return;
      }
      setAddressData([]);
      setAddressLookupError(t("editContactDetails.errors.postcodeRequired"));
    } catch (error: any) {
      setAddressData([]);
      setAddressLookupError(getLookupErrorMessage(error));
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    const fetchCurrentAddresses = async () => {
      if (isAddressManual) return;
      if (!debouncedPostcode) {
        if (dirtyFields.address?.postcodeLookup) {
          setAddressData([]);

          clearAddressFields();
        }
        return;
      }
      fetchAddresses(debouncedPostcode);
    };
    fetchCurrentAddresses();
  }, [debouncedPostcode]);

  const mapStreetName = (addressData: AddressResult) => {
    const streetParts = [
      addressData.organization_qsav,
      addressData.subbuilding_qsav,
      addressData.building_qsav,
      [addressData.housenumber_qsav, addressData.street_qsav].filter(Boolean).join(" "),
    ].filter(Boolean);

    return streetParts.join(", ");
  };

  const handleAddressFieldSelect = (
    value: { value: string; label: string; }
  ) => {
    setValue("address.addressSelector", value.value, { shouldValidate: true });
    const selectedAddress = addressData.find((address) => address.address_qsav === value.value);
    if (selectedAddress) {
      setValue("address.addressLine1", mapStreetName(selectedAddress));
      setValue("address.addressLine2", selectedAddress.dependentlocality_qsav);
      setValue("address.city", selectedAddress.locality_qsav);
      setValue("address.county", selectedAddress.administrativearea_qsav);
      setValue("address.postCode", selectedAddress.postcode_qsav);
      trigger([
        "address.addressLine1",
        "address.city",
        "address.county",
        "address.postCode",
      ]);
    }
  };

  const { errors } = useFormState({
    control,
    name: [
      "address.addressLine1",
      "address.addressLine2",
      "address.city",
      "address.county",
      "address.postCode",
      "address.country",
    ],
  });

  useEffect(() => {
    if (errors.address) {
      setAddressLookupError(t("editContactDetails.errors.addressRequired"));
      setIsAddressManual(true);
    }
  }, [errors.address, t]);

  return (
    <div className="flex flex-col gap-5" data-testid="addressSection">
      {isAddressManual ?
        <AddressManualEntry toggleAddressType={() => setIsAddressManual(!isAddressManual)} /> :
        <AddressLookup
          addressOptions={addressData.map((address) => ({
            label: address.address_qsav,
            value: address.address_qsav,
          }))}
          onSelectedAddressChange={(value) => handleAddressFieldSelect(value)}
          toggleAddressType={() => setIsAddressManual(!isAddressManual)}
          errorMessage={addressLookupError}
          isFetching={isFetching}
        />
      }
      <div className="flex flex-col gap-2">
        <Text.P size="large" fontWeight="semi-bold">{t("editContactDetails.nonUkTitle")}</Text.P>
        <Text.P size="large"><Trans i18nKey={"editContactDetails.nonUkInfo"} components={translationMapping(t)} /> </Text.P>
      </div>
    </div>
  );
};

export default AddressSection;
